import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { adminApi, formatApiError } from "./api";
import { toast } from "sonner";
import { ROLE_STYLE, StatusCell, VerificationCell, Stars, money, timeAgo, UserAvatar } from "./userCells";
import { CheckCircle2, Send, Loader2, IdCard, Camera, ShieldCheck, XCircle, ImageOff } from "lucide-react";

export function UserProfileDialog({ user, onClose }) {
  if (!user) return null;
  const rs = ROLE_STYLE[user.role] || ROLE_STYLE.rider;
  const row = (label, value) => (
    <div className="flex justify-between border-b border-[#12142b]/6 py-2.5 text-sm last:border-0">
      <span className="text-[#565b73]">{label}</span>
      <span className="font-medium text-[#12142b]">{value}</span>
    </div>
  );
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="user-profile-dialog">
        <DialogHeader>
          <DialogTitle>User Profile</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-4">
          <UserAvatar name={user.name} photo={user.photo} size="h-16 w-16 text-xl" testId="user-dialog-avatar" />
          <div>
            <div className="flex items-center gap-2">
              <p className="font-display text-lg font-bold text-[#12142b]">{user.name}</p>
              {user.verified_badge && <CheckCircle2 className="h-4 w-4 text-[#2340b5]" />}
            </div>
            <p className="text-sm text-[#565b73]">{user.user_code}</p>
            <span className="mt-1 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize"
              style={{ color: rs.color, backgroundColor: rs.bg }}>{rs.label}</span>
          </div>
        </div>
        <div className="mt-4">
          {row("Email", user.email)}
          {row("Phone", user.phone)}
          {row("Province / City", `${user.province} · ${user.city}`)}
          {row("Status", <StatusCell status={user.status} online={user.online} />)}
          {row("Verification", <VerificationCell verification={user.verification} />)}
          {row("Trips", `${user.trips_completed} completed · ${user.trips_upcoming} upcoming`)}
          {row("Rating", <Stars rating={user.rating} count={user.rating_count} />)}
          {row("Wallet", money(user.wallet))}
          {row("Earnings", money(user.earnings))}
          {row("Joined", new Date(user.joined_at).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit" }))}
          {row("Last active", timeAgo(user.last_active_min))}
          {user.notes?.length > 0 && row("Admin notes", `${user.notes.length}`)}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AddUserDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "rider", province: "AB" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.post("/users", form);
      toast.success("User created");
      setForm({ name: "", email: "", phone: "", role: "rider", province: "AB" });
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  const field = "mt-1 w-full rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 py-2.5 text-sm outline-none focus:border-[#2340b5]";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="add-user-dialog">
        <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-sm font-semibold text-[#12142b]">Full name</label>
            <input data-testid="add-user-name" required value={form.name} onChange={set("name")} className={field} placeholder="Jane Doe" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#12142b]">Email</label>
            <input data-testid="add-user-email" required type="email" value={form.email} onChange={set("email")} className={field} placeholder="jane@example.com" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#12142b]">Phone</label>
            <input data-testid="add-user-phone" value={form.phone} onChange={set("phone")} className={field} placeholder="+1 403 555 1234" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold text-[#12142b]">Role</label>
              <select data-testid="add-user-role" value={form.role} onChange={set("role")} className={field}>
                <option value="rider">Rider</option>
                <option value="driver">Driver</option>
                <option value="business">Business</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-[#12142b]">Province</label>
              <select data-testid="add-user-province" value={form.province} onChange={set("province")} className={field}>
                <option value="AB">Alberta</option>
                <option value="SK">Saskatchewan</option>
                <option value="MB">Manitoba</option>
              </select>
            </div>
          </div>
          <DialogFooter className="mt-2">
            <button type="button" onClick={onClose} className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#565b73]">Cancel</button>
            <button data-testid="add-user-submit" disabled={loading} className="rounded-xl bg-[#2340b5] px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60">
              {loading ? "Creating…" : "Create User"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AddNoteDialog({ user, onClose, onSaved }) {
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.post(`/users/${user.id}/note`, { note });
      toast.success("Note added");
      setNote("");
      onSaved?.();
      onClose();
    } catch {
      toast.error("Failed to add note");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="add-note-dialog">
        <DialogHeader><DialogTitle>Add Admin Note{user ? ` — ${user.name}` : ""}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <textarea data-testid="note-text" required value={note} onChange={(e) => setNote(e.target.value)} rows={4}
            className="w-full rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 py-2.5 text-sm outline-none focus:border-[#2340b5]"
            placeholder="Internal note about this user…" />
          <DialogFooter>
            <button type="button" onClick={onClose} className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#565b73]">Cancel</button>
            <button data-testid="note-submit" disabled={loading} className="rounded-xl bg-[#2340b5] px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60">
              {loading ? "Saving…" : "Save Note"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


export function SendMessageDialog({ user, onClose }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setLoading(true);
    try {
      await adminApi.post(`/users/${user.id}/message`, { text: text.trim() });
      toast.success(`Message sent to ${user.name}`);
      setText("");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send message");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="send-message-dialog">
        <DialogHeader>
          <DialogTitle>Send Message{user ? ` — ${user.name}` : ""}</DialogTitle>
          <p className="text-sm text-[#565b73]">This message is delivered to the user in the SafeRide app.</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <textarea
            data-testid="message-text"
            required
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            maxLength={1000}
            className="w-full rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 py-2.5 text-sm outline-none focus:border-[#2340b5]"
            placeholder="Type your message to the user…"
          />
          <DialogFooter>
            <button type="button" onClick={onClose} className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#565b73]">Cancel</button>
            <button data-testid="message-submit" disabled={loading || !text.trim()} className="flex items-center gap-2 rounded-xl bg-[#2340b5] px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Message
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const DocImage = ({ src, label, Icon }) => {
  const [broken, setBroken] = useState(false);
  const valid = src && src.length > 30 && !broken;
  return (
    <div className="flex-1">
      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-[#565b73]"><Icon className="h-3.5 w-3.5" /> {label}</p>
      <div className="grid aspect-[4/3] w-full place-items-center overflow-hidden rounded-xl border border-[#12142b]/10 bg-[#f4f6fb]">
        {valid ? (
          <img src={src} alt={label} className="h-full w-full object-contain" onError={() => setBroken(true)} />
        ) : (
          <div className="flex flex-col items-center gap-1 text-[#c3cbe4]">
            <ImageOff className="h-8 w-8" />
            <span className="text-xs text-[#565b73]">No image available</span>
          </div>
        )}
      </div>
    </div>
  );
};

export function KycDialog({ user, onClose, onChanged }) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const id = user?.id;

  const load = useCallback(() => {
    if (!id) return;
    setDoc(null);
    setErr("");
    adminApi.get(`/users/${id}/kyc`)
      .then((r) => setDoc(r.data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "No documents submitted for this user"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const decide = async (approve) => {
    setBusy(approve ? "approve" : "reject");
    try {
      await adminApi.post(`/users/${id}/${approve ? "verify" : "reject-kyc"}`);
      toast.success(approve ? `${user.name} verified` : `${user.name}'s documents rejected`);
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    } finally {
      setBusy("");
    }
  };

  const st = doc?.status || "pending";
  const resolved = st === "verified" || st === "approved" || st === "rejected";

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="kyc-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#2340b5]" /> Verify Documents{user ? ` — ${user.name}` : ""}</DialogTitle>
          <p className="text-sm text-[#565b73]">
            {doc?.submitted_at ? `Submitted ${new Date(doc.submitted_at).toLocaleString("en-CA", { month: "short", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : "Review the ID and selfie submitted for verification."}
            {doc ? ` · Status: ${st}` : ""}
          </p>
        </DialogHeader>

        {!doc && !err && <p className="py-10 text-center text-sm text-[#565b73]">Loading documents…</p>}
        {err && <p className="py-10 text-center text-sm text-[#e4002b]" data-testid="kyc-error">{err}</p>}
        {doc && (
          <>
            <div className="flex flex-col gap-4 sm:flex-row">
              <DocImage src={doc.id_image} label="Government ID" Icon={IdCard} />
              <DocImage src={doc.selfie_image} label="Selfie" Icon={Camera} />
            </div>
            <DialogFooter>
              <button
                data-testid="kyc-reject"
                disabled={!!busy}
                onClick={() => decide(false)}
                className="flex items-center gap-2 rounded-xl border border-[#e4002b]/30 px-4 py-2.5 text-sm font-semibold text-[#e4002b] transition-colors hover:bg-[#e4002b]/8 disabled:opacity-50"
              >
                {busy === "reject" ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />} Reject
              </button>
              <button
                data-testid="kyc-approve"
                disabled={!!busy}
                onClick={() => decide(true)}
                className="flex items-center gap-2 rounded-xl bg-[#16a34a] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#15803d] disabled:opacity-50"
              >
                {busy === "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Approve &amp; Verify
              </button>
            </DialogFooter>
            {resolved && <p className="text-center text-xs text-[#565b73]">This submission is already {st}. You can still re-decide.</p>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


export function UserTripsDialog({ user, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setData(null);
    setError("");
    if (user) {
      adminApi.get(`/users/${user.id}/trips`)
        .then((r) => setData(r.data))
        .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Could not load trips"));
    }
  }, [user]);
  if (!user) return null;
  const trips = data?.trips || [];
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="user-trips-dialog">
        <DialogHeader>
          <DialogTitle>Trip history — {user.name}</DialogTitle>
        </DialogHeader>
        {error && <p className="text-sm font-semibold text-[#e4002b]" data-testid="user-trips-error">{error}</p>}
        {!data && !error && <p className="py-6 text-center text-sm text-[#565b73]">Loading trips…</p>}
        {data && (
          <div className="max-h-[420px] overflow-y-auto">
            {trips.length === 0 ? (
              <p className="py-8 text-center text-sm text-[#565b73]" data-testid="user-trips-empty">No trips found for this user.</p>
            ) : (
              <table className="w-full text-left text-sm" data-testid="user-trips-table">
                <thead>
                  <tr className="border-b border-[#12142b]/8 text-xs uppercase tracking-wide text-[#565b73]">
                    <th className="pb-2 font-semibold">Date</th>
                    <th className="pb-2 font-semibold">Route</th>
                    <th className="pb-2 font-semibold">As</th>
                    <th className="pb-2 font-semibold">Status</th>
                    <th className="pb-2 text-right font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {trips.map((t, i) => (
                    <tr key={`${t.id}-${i}`} data-testid={`user-trip-${i}`} className="border-b border-[#12142b]/5 last:border-0">
                      <td className="py-2.5 text-[#565b73]">{t.date ? new Date(t.date).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                      <td className="py-2.5 font-medium text-[#12142b]">{t.route}</td>
                      <td className="py-2.5 text-[#565b73]">{t.role}</td>
                      <td className="py-2.5"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${t.status === "completed" ? "bg-[#16a34a]/10 text-[#16a34a]" : t.status.includes("cancel") ? "bg-[#e4002b]/10 text-[#e4002b]" : "bg-[#2340b5]/10 text-[#2340b5]"}`}>{t.status}</span></td>
                      <td className="py-2.5 text-right font-semibold text-[#12142b]">{money(t.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function UserWalletDialog({ user, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setData(null);
    setError("");
    if (user) {
      adminApi.get(`/users/${user.id}/wallet`)
        .then((r) => setData(r.data))
        .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Could not load wallet"));
    }
  }, [user]);
  if (!user) return null;
  const history = data?.history || [];
  const card = (label, value, testid, accent) => (
    <div className="rounded-xl bg-[#f4f6fb] p-4 text-center" data-testid={testid}>
      <p className="font-display text-xl font-black tracking-tight" style={{ color: accent || "#12142b" }}>{money(value)}</p>
      <p className="text-xs font-semibold text-[#565b73]">{label}</p>
    </div>
  );
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="user-wallet-dialog">
        <DialogHeader>
          <DialogTitle>Wallet & Payments — {user.name}</DialogTitle>
        </DialogHeader>
        {error && <p className="text-sm font-semibold text-[#e4002b]" data-testid="user-wallet-error">{error}</p>}
        {!data && !error && <p className="py-6 text-center text-sm text-[#565b73]">Loading wallet…</p>}
        {data && (
          <>
            <div className="grid grid-cols-3 gap-3">
              {card("Wallet balance", data.available_balance, "wallet-balance", "#2340b5")}
              {card("Available to withdraw", data.available_to_withdraw, "wallet-withdraw", "#16a34a")}
              {card("Paid out to date", data.total_paid_out, "wallet-paid-out")}
            </div>
            <div className="mt-4 max-h-[320px] overflow-y-auto">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-[#565b73]">Payment history</p>
              {history.length === 0 ? (
                <p className="py-6 text-center text-sm text-[#565b73]" data-testid="wallet-history-empty">No transactions yet.</p>
              ) : (
                <table className="w-full text-left text-sm" data-testid="wallet-history-table">
                  <thead>
                    <tr className="border-b border-[#12142b]/8 text-xs uppercase tracking-wide text-[#565b73]">
                      <th className="pb-2 font-semibold">Date</th>
                      <th className="pb-2 font-semibold">Type</th>
                      <th className="pb-2 font-semibold">Detail</th>
                      <th className="pb-2 font-semibold">Status</th>
                      <th className="pb-2 text-right font-semibold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={`${h.id}-${i}`} data-testid={`wallet-tx-${i}`} className="border-b border-[#12142b]/5 last:border-0">
                        <td className="py-2.5 text-[#565b73]">{h.date ? new Date(h.date).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                        <td className="py-2.5 font-medium text-[#12142b]">{h.kind}</td>
                        <td className="py-2.5 text-[#565b73]">{h.detail || "—"}</td>
                        <td className="py-2.5 text-[#565b73]">{h.status}</td>
                        <td className="py-2.5 text-right font-semibold" style={{ color: h.amount < 0 ? "#e4002b" : "#12142b" }}>{h.amount < 0 ? `−${money(-h.amount)}` : money(h.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
